import { Component, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators, AbstractControl } from "@angular/forms";
import { CreateDeviceRequest } from './devices.service';
import { AuthService } from '../../core/auth/auth.service';
import { Camera } from './bots.component';

@Component({
  selector: 'app-create-bot-dialog',
  templateUrl: './create-bot-dialog.component.html',
  styleUrls: ['./create-bot-dialog.component.scss'],
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule
  ],
  standalone: true,
})
export class CreateBotDialogComponent implements OnDestroy {
  private authService = inject(AuthService);
  
  createBotForm: FormGroup;
  imagePreview: string | null = null;
  selectedImageFile: File | null = null;

  constructor(public dialogRef: MatDialogRef<CreateBotDialogComponent>, private fb: FormBuilder) {
    this.createBotForm = this.fb.group({
      name: ['', Validators.required],
      imageFile: [null],
      password: ['', Validators.required],
      cameras: this.fb.array([])
    });
  }

  get cameras(): FormArray {
    return this.createBotForm.get('cameras') as FormArray;
  }

  addCamera(): void {
    const cameraForm = this.fb.group({
      name: ['', Validators.required]
    });
    this.cameras.push(cameraForm);
  }

  removeCamera(index: number): void {
    this.cameras.removeAt(index);
  }

  getCameraControl(index: number): AbstractControl | null {
    return this.cameras.at(index);
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      this.selectedImageFile = file;
      
      // Create preview URL
      if (this.imagePreview) {
        URL.revokeObjectURL(this.imagePreview);
      }
      this.imagePreview = URL.createObjectURL(file);
      
      // Update form control
      this.createBotForm.patchValue({
        imageFile: file
      });
      this.createBotForm.get('imageFile')?.updateValueAndValidity();
    }
  }

  removeImage(): void {
    if (this.imagePreview) {
      URL.revokeObjectURL(this.imagePreview);
    }
    this.imagePreview = null;
    this.selectedImageFile = null;
    this.createBotForm.patchValue({
      imageFile: null
    });
    this.createBotForm.get('imageFile')?.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    // Clean up object URL to prevent memory leaks
    if (this.imagePreview) {
      URL.revokeObjectURL(this.imagePreview);
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    this.createBotForm.markAllAsTouched();
    if (this.createBotForm.valid) {
      const formValue = this.createBotForm.value;
      const user = this.authService.user();
      
      // Map cameras to the expected format
      const cameras: Camera[] = formValue.cameras.map((camera: { name: string }, index: number) => ({
        id: `temp-${index}`,
        name: camera.name
      }));

      // Prepare bot data to send to parent component
      const botData: CreateDeviceRequest & { adminId?: string; imagePreview?: string | null } = {
        name: formValue.name,
        status: 'offline',
        cameras: cameras,
        imageFile: this.selectedImageFile ?? undefined,
        password: formValue.password,
        adminId: user?.userProfile?.id?.toString() || '',
        imagePreview: this.imagePreview // Temporary preview URL for display
      };

      // Emit the bot data back to parent component through dialog close event
      this.dialogRef.close({ 
        submitted: true, 
        deviceData: botData 
      });
    }
  }
}

